import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, ExternalLink, Search } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetTitle,
} from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { ScrollArea, ScrollAreaViewport } from '@/components/ui/scroll-area';
import { fetchCategoryProjectUsage } from '@/lib/data-service';
import type { CategoryProjectUsageItem } from '@/lib/data-service';
import type { GlobalCategory } from '@/types/category.types';
import { ROUTES } from '@/routes';

interface CategoryProjectsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  category: GlobalCategory | null;
}

function healthClassName(health: string): string {
  switch (health?.toLowerCase()) {
    case 'green':
      return 'border-success-500 text-success-700 bg-success-50';
    case 'amber':
      return 'border-warning-500 text-warning-700 bg-warning-50';
    case 'red':
      return 'border-error-500 text-error-700 bg-error-50';
    default:
      return '';
  }
}

export function CategoryProjectsSheet({ open, onOpenChange, category }: CategoryProjectsSheetProps) {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<CategoryProjectUsageItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (!open || !category) {
      setProjects([]);
      setSearchQuery('');
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    fetchCategoryProjectUsage(category.id)
      .then((data) => {
        if (!cancelled) setProjects(data);
      })
      .catch(() => {
        if (!cancelled) setProjects([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => { cancelled = true; };
  }, [open, category]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-2xl p-0 flex flex-col h-full">
        {/* Fixed Header */}
        <div className="border-b bg-background p-6 pb-4 flex-shrink-0">
          <div className="space-y-1">
            <SheetTitle className="text-2xl font-bold">
              Projects using {category?.name ?? 'Category'}
            </SheetTitle>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              {category?.code && (
                <>
                  <span className="font-sans font-medium">{category.code}</span>
                  <span>•</span>
                </>
              )}
              <span>{category?.type}</span>
              <span>•</span>
              <span>
                {isLoading
                  ? 'Loading...'
                  : `${projects.length} project${projects.length !== 1 ? 's' : ''}`}
              </span>
            </div>
          </div>

          {/* Search */}
          {!isLoading && projects.length > 0 && (
            <div className="relative mt-3">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
              <Input
                className="pl-8 h-8 text-sm"
                placeholder="Search projects..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          )}
        </div>

        {/* Scrollable Content */}
        <ScrollArea className="flex-1 min-h-0">
          <ScrollAreaViewport className="h-full max-h-full">
            <div className="p-6 space-y-4">
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : projects.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <p className="text-sm">No projects are using this category.</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {projects
                    .filter((p) => {
                      if (!searchQuery.trim()) return true;
                      const q = searchQuery.toLowerCase();
                      return (
                        p.name.toLowerCase().includes(q) ||
                        p.code?.toLowerCase().includes(q) ||
                        p.type.toLowerCase().includes(q)
                      );
                    })
                    .map((project, index, filtered) => (
                    <div key={project.id}>
                      <button
                        className="w-full text-left rounded-lg p-3 hover:bg-muted/50 transition-colors cursor-pointer"
                        onClick={() => {
                          onOpenChange(false);
                          navigate(ROUTES.PROJECT_DETAILS(project.id));
                        }}
                      >
                        <div className="flex items-center gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm truncate">{project.name}</div>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                              {project.code && (
                                <span className="font-sans font-medium">{project.code}</span>
                              )}
                              {project.code && project.type && <span>•</span>}
                              {project.type && <span>{project.type}</span>}
                            </div>
                          </div>
                          {project.health && (
                            <Badge
                              variant="outline"
                              className={`text-xs ${healthClassName(project.health)}`}
                            >
                              {project.health}
                            </Badge>
                          )}
                          <ExternalLink className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                        </div>
                      </button>
                      {index < filtered.length - 1 && <Separator />}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </ScrollAreaViewport>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
